import { useCallback, useEffect, useRef, useState } from "react";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { FolderOpen, FileText, UploadSimple, Eye, DownloadSimple, Trash, Lock, LockOpen, MagnifyingGlass, Archive, ArrowCounterClockwise } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const fmtSize = (b) => b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : b > 1024 ? `${Math.round(b / 1024)} KB` : `${b} B`;

export default function DmsBrowser({ pid }) {
  const { user } = useAuth();
  const [tree, setTree] = useState([]);
  const [folder, setFolder] = useState("");
  const [files, setFiles] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [checked, setChecked] = useState(new Set());
  const [dup, setDup] = useState(null);   // duplicate prompt state
  const [stats, setStats] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const fileRef = useRef(null);

  const loadTree = useCallback(async () => {
    if (!pid) return;
    try { const { data } = await api.get(`/dms/${pid}/tree`); setTree(data.folders); }
    catch (e) { toast.error(fmtErr(e)); }
  }, [pid]);

  const loadFiles = useCallback(async () => {
    if (!pid) return;
    try {
      const { data } = await api.get(`/dms/${pid}/files`, { params: { folder, q, show_deleted: showDeleted } });
      setFiles(data.files);
      setSel((s) => (s ? data.files.find((x) => x.id === s.id) || null : s));
    } catch (e) { toast.error(fmtErr(e)); }
  }, [pid, folder, q, showDeleted]);

  useEffect(() => { loadTree(); api.get("/dms/stats").then((r) => setStats(r.data)).catch(() => {}); }, [loadTree]);
  useEffect(() => { setSel(null); setChecked(new Set()); loadFiles(); }, [loadFiles]);

  const doUpload = async (file, mode = "ask") => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", folder || "09-correspondence");
    fd.append("mode", mode);
    const upId = `${file.name}-${Date.now()}`;
    setUploads((u) => [...u, { id: upId, name: file.name, pct: 0 }]);
    try {
      const { data } = await api.post(`/dms/${pid}/upload`, fd, {
        timeout: 120000,
        onUploadProgress: (e) => setUploads((u) => u.map((x) => x.id === upId
          ? { ...x, pct: Math.round((e.loaded / (e.total || file.size || 1)) * 100) } : x)),
      });
      if (data.ok) {
        toast.success(`Uploaded ${file.name}`);
        setDup(null); loadFiles(); loadTree();
      } else {
        setDup({ file, ...data });
      }
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setUploads((u) => u.filter((x) => x.id !== upId)); }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) doUpload(f);
    e.target.value = "";
  };

  const zipSelected = async () => {
    try {
      const { data } = await api.post(`/dms/${pid}/zip`, { file_ids: [...checked] }, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data);
      a.download = "documents.zip";
      a.click();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const del = async (f) => {
    try { await api.delete(`/dms/file/${f.id}`); toast.success("Moved to trash"); loadFiles(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const restore = async (f) => {
    try { await api.post(`/dms/file/${f.id}/restore`); toast.success("Restored"); loadFiles(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const toggleLock = async (f) => {
    try {
      if (f.acl?.locked && f.acl?.locked_by === user?.id) {
        toast.info("Upload a new version via check-in to unlock");
        return;
      }
      await api.post(`/dms/file/${f.id}/checkout`);
      toast.success("Checked out — locked for editing by you (24h)");
      loadFiles();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const checkin = async (f, file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("notes", "Check-in from file browser");
    try {
      await api.post(`/dms/file/${f.id}/checkin`, fd, { timeout: 120000 });
      toast.success(`Checked in as v${f.current_version + 1}`);
      loadFiles();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="flex gap-4 items-start" data-testid="dms-browser">
      {/* folder tree */}
      <div className="w-64 shrink-0 bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm" data-testid="dms-tree">
        {tree.map((f) => (
          <button key={f.id} data-testid={`dms-folder-${f.id}`} onClick={() => setFolder(f.id === folder ? "" : f.id)}
            className={`w-full flex items-center gap-2 px-3 py-2 text-[11px] text-left border-b border-[var(--c-border)]/50 last:border-0 ${
              folder === f.id ? "bg-[var(--c-accent)]/15 text-[var(--c-text)]" : "text-[var(--c-muted)] hover:text-[var(--c-text)]"}`}>
            <FolderOpen size={12} className="text-[var(--c-amber)] shrink-0" />
            <span className="truncate flex-1">{f.name}</span>
            <span className="font-mono text-[9px] shrink-0">{f.count}</span>
          </button>
        ))}
      </div>

      {/* file list */}
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={11} className="absolute left-2 top-2 text-[var(--c-muted)]" />
            <input data-testid="dms-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search files, tags, content…"
              className="w-64 h-7 pl-7 bg-[var(--c-surface)] border border-[var(--c-border)] text-[var(--c-text)] text-[11px] rounded-sm outline-none focus:border-[var(--c-accent)]" />
          </div>
          <button data-testid="dms-upload-btn" onClick={() => fileRef.current?.click()}            className="h-7 px-2.5 bg-[var(--c-accent)] text-white text-[11px] rounded-sm flex items-center gap-1.5">
            <UploadSimple size={12} /> Upload
          </button>
          <input ref={fileRef} type="file" className="hidden" onChange={onPick} data-testid="dms-file-input" />
          {checked.size > 0 && (
            <button data-testid="dms-zip-btn" onClick={zipSelected}
              className="h-7 px-2.5 border border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] text-[11px] rounded-sm flex items-center gap-1.5">
              <Archive size={12} /> ZIP ({checked.size})
            </button>
          )}
          {user?.role === "admin" && (
            <label className="ml-auto flex items-center gap-1.5 text-[10px] text-[var(--c-muted)]">
              <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} data-testid="dms-show-deleted" />
              Show deleted
            </label>
          )}
        </div>

        {/* duplicate dialog */}
        {dup && (
          <div className="bg-[var(--c-amber)]/10 border border-[var(--c-amber)]/40 rounded-sm p-3" data-testid="dms-dup-dialog">
            <div className="text-[11px] text-[var(--c-text)]">
              {dup.duplicate_content
                ? `⚠ Identical content already exists: ${dup.existing.filename} (${dup.existing.folder_path})`
                : `A file named "${dup.existing.filename}" already exists in this folder.`}
            </div>
            <div className="flex gap-2 mt-2">
              {dup.duplicate_name && (
                <button data-testid="dup-replace" onClick={() => doUpload(dup.file, "replace")}
                  className="h-6 px-2.5 bg-[var(--c-accent)] text-white text-[10px] rounded-sm">Replace (new version)</button>
              )}
              {dup.duplicate_name && (
                <button data-testid="dup-rename" onClick={() => doUpload(dup.file, "rename")}
                  className="h-6 px-2.5 border border-[var(--c-border)] text-[var(--c-muted)] text-[10px] rounded-sm">Rename & upload</button>
              )}
              {dup.duplicate_content && (
                <button data-testid="dup-anyway" onClick={() => doUpload(dup.file, "anyway")}
                  className="h-6 px-2.5 bg-[var(--c-accent)] text-white text-[10px] rounded-sm">Upload anyway</button>
              )}
              <button data-testid="dup-cancel" onClick={() => setDup(null)}
                className="h-6 px-2.5 text-[var(--c-muted)] text-[10px]">Cancel</button>
            </div>
          </div>
        )}

        <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm overflow-x-auto">
        {uploads.length > 0 && (
          <div className="space-y-1" data-testid="dms-upload-progress">
            {uploads.map((u) => (
              <div key={u.id} className="flex items-center gap-2" data-testid={`dms-up-${u.id}`}>
                <span className="text-[10px] text-[var(--c-muted)] truncate w-56">{u.name}</span>
                <div className="flex-1 h-1.5 bg-[var(--c-bg)] rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-200 ${u.pct >= 100 ? "bg-[var(--c-green)]" : "bg-[var(--c-accent)]"}`}
                    style={{ width: `${u.pct}%` }} />
                </div>
                <span className="text-[9px] font-mono text-[var(--c-muted)] w-8 text-right">{u.pct}%</span>
              </div>
            ))}
          </div>
        )}
          <table className="w-full" data-testid="dms-file-list">
            <thead>
              <tr className="border-b border-[var(--c-border)] text-left font-mono text-[9px] uppercase tracking-wider text-[var(--c-muted)]">
                <th className="py-2 px-2 w-6"></th><th className="py-2 px-2">Name</th><th className="py-2 px-2">Type</th>
                <th className="py-2 px-2 text-right">Size</th><th className="py-2 px-2">Ver</th>
                <th className="py-2 px-2">Modified</th><th className="py-2 px-2">Source</th><th className="py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {files.map((f) => (
                <tr key={f.id} onClick={() => setSel(f)}
                  className={`border-b border-[var(--c-border)]/40 cursor-pointer ${sel?.id === f.id ? "bg-[var(--c-accent)]/10" : "hover:bg-[var(--c-border)]/25"} ${f.status === "deleted" ? "opacity-40 line-through" : ""}`}
                  data-testid={`dms-row-${f.id}`}>
                  <td className="py-1.5 px-2" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={checked.has(f.id)} data-testid={`dms-check-${f.id}`}
                      onChange={() => setChecked((c) => { const n = new Set(c); n.has(f.id) ? n.delete(f.id) : n.add(f.id); return n; })} />
                  </td>
                  <td className="py-1.5 px-2 text-[11px] text-[var(--c-text)]">
                    <span className="flex items-center gap-1.5">
                      <FileText size={11} className="text-[var(--c-muted)] shrink-0" />{f.display_name}
                      {f.acl?.locked && <Lock size={10} className="text-[var(--c-amber)]" title={`Checked out by ${f.acl.locked_by_name}`} />}
                    </span>
                  </td>
                  <td className="py-1.5 px-2 font-mono text-[10px] text-[var(--c-muted)] uppercase">{f.file_type}</td>
                  <td className="py-1.5 px-2 text-right font-mono text-[10px] text-[var(--c-muted)]">{fmtSize(f.size_bytes || 0)}</td>
                  <td className="py-1.5 px-2 font-mono text-[10px] text-[var(--c-accent)]">v{f.current_version}</td>
                  <td className="py-1.5 px-2 font-mono text-[10px] text-[var(--c-muted)]">{String(f.updated_at || "").slice(0, 10)}</td>
                  <td className="py-1.5 px-2 text-[9px] text-[var(--c-muted)]">{(f.source?.generator || f.source?.type || "manual").replace(/_/g, " ")}</td>
                  <td className="py-1.5 px-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <a href={`${BASE}/api/dms/file/${f.id}/preview`} target="_blank" rel="noreferrer" title="Preview"
                        data-testid={`dms-preview-${f.id}`} className="text-[var(--c-muted)] hover:text-[var(--c-accent)]"><Eye size={12} /></a>
                      <a href={`${BASE}/api/dms/file/${f.id}/download`} title="Download"
                        data-testid={`dms-download-${f.id}`} className="text-[var(--c-muted)] hover:text-[var(--c-accent)]"><DownloadSimple size={12} /></a>
                      {f.status === "active" && ["engineer", "lead", "admin"].includes(user?.role) && (
                        <button onClick={() => toggleLock(f)} title={f.acl?.locked ? "Checked out" : "Check out"}
                          data-testid={`dms-lock-${f.id}`} className="text-[var(--c-muted)] hover:text-[var(--c-amber)]">
                          {f.acl?.locked ? <Lock size={12} /> : <LockOpen size={12} />}
                        </button>
                      )}
                      {f.status === "active" && ["lead", "admin"].includes(user?.role) && (
                        <button onClick={() => del(f)} title="Delete" data-testid={`dms-delete-${f.id}`}
                          className="text-[var(--c-muted)] hover:text-[var(--c-red)]"><Trash size={12} /></button>
                      )}
                      {f.status === "deleted" && user?.role === "admin" && (
                        <button onClick={() => restore(f)} title="Restore" data-testid={`dms-restore-${f.id}`}
                          className="text-[var(--c-green)]"><ArrowCounterClockwise size={12} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!files.length && (
                <tr><td colSpan={8} className="py-8 text-center text-[11px] text-[var(--c-muted)]" data-testid="dms-empty">
                  No files{folder ? " in this folder" : ""}. Generated drawings, BOM exports and design sheets auto-file here.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        {stats && (
          <div className="text-[9px] font-mono text-[var(--c-muted)]" data-testid="dms-storage-footer">
            Storage: {fmtSize(stats.total_bytes)} / {fmtSize(stats.quota_bytes)} | {stats.file_count} files
          </div>
        )}
      </div>

      {/* details panel */}
      {sel && (
        <div className="w-72 shrink-0 bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-3 space-y-2" data-testid="dms-details">
          <div className="text-[12px] text-[var(--c-text)] font-medium break-all">{sel.display_name}</div>
          {[["Type", sel.file_type?.toUpperCase()], ["Size", fmtSize(sel.size_bytes || 0)],
            ["Folder", sel.folder_path], ["File ID", sel.file_id],
            ["Hash", (sel.content_hash || "").slice(0, 18) + "…"],
            ["Source", sel.source?.generator || sel.source?.type || "manual upload"]].map(([k, v]) => (
            <div key={k} className="flex justify-between text-[10px] gap-2">
              <span className="text-[var(--c-muted)]">{k}</span><span className="font-mono text-[var(--c-text)] text-right truncate">{v}</span>
            </div>
          ))}
          <div className="border-t border-[var(--c-border)] pt-2">
            <div className="text-[9px] font-mono uppercase tracking-wider text-[var(--c-muted)] mb-1">Versions</div>
            {[...sel.versions].reverse().map((v) => (
              <div key={v.version} className="flex items-center gap-2 text-[10px] py-0.5" data-testid={`dms-version-${v.version}`}>
                <span className="font-mono text-[var(--c-accent)]">v{v.version}</span>
                <span className="text-[var(--c-muted)] flex-1 truncate">{String(v.uploaded_at).slice(0, 10)} · {v.uploaded_by}</span>
                <a href={`${BASE}/api/dms/file/${sel.id}/download?version=${v.version}`}
                  className="text-[var(--c-muted)] hover:text-[var(--c-accent)]"><DownloadSimple size={10} /></a>
              </div>
            ))}
          </div>
          {sel.acl?.locked && sel.acl?.locked_by === user?.id && (
            <label className="block">
              <span className="h-6 w-full flex items-center justify-center bg-[var(--c-accent)] text-white text-[10px] rounded-sm cursor-pointer" data-testid="dms-checkin-btn">
                Check in new version…
              </span>
              <input type="file" className="hidden" data-testid="dms-checkin-input"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) checkin(sel, f); e.target.value = ""; }} />
            </label>
          )}
          {sel.tags?.length > 0 && (
            <div className="flex gap-1 flex-wrap">
              {sel.tags.map((t) => <span key={t} className="text-[9px] font-mono px-1.5 py-0.5 bg-[var(--c-border)]/60 text-[var(--c-muted)] rounded-sm">{t}</span>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
